import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, BookOpen, Star, ShieldCheck, ArrowRight, Clock, Users } from 'lucide-react';
const buyers = [{
  name: 'Laura',
  city: 'Córdoba'
}, {
  name: 'Sofía',
  city: 'Rosario'
}, {
  name: 'Martina',
  city: 'CABA'
}, {
  name: 'Carolina',
  city: 'Mendoza'
}, {
  name: 'Julieta',
  city: 'La Plata'
}, {
  name: 'Agustina',
  city: 'Mar del Plata'
}, {
  name: 'Camila',
  city: 'Tucumán'
}];
const testimonials = [{
  name: 'Lic. Ana M.',
  role: 'Psicóloga Clínica',
  text: 'Desde que uso la Biblioteca, mis sesiones con pacientes dependientes dieron un giro de 180 grados. Tener la pregunta exacta en el momento justo me dio muchísima seguridad.'
}, {
  name: 'Lic. Valeria R.',
  role: 'Psicoterapeuta',
  text: 'El tomo de Autoexigencia es una obra maestra. Mis pacientes por fin están logrando internalizar sus logros sin sentir que son un fraude. Lo recomiendo a ciegas.'
}, {
  name: 'Lic. Florencia T.',
  role: 'Psicóloga',
  text: "Siempre me costaba trabajar los límites con mujeres que fueron criadas para complacer. Las fichas de intervención me resolvieron el 'cómo' llevarlo a la práctica."
}, {
  name: 'Lic. Mariana G.',
  role: 'Psicóloga Clínica',
  text: 'Una herramienta indispensable. No es solo teoría, es la práctica pura que nos falta cuando salimos de la facultad. Vale cada centavo.'
}, {
  name: 'Lic. Luciana P.',
  role: 'Terapeuta Cognitivo Conductual',
  text: 'Compré la biblioteca buscando ideas y me encontré con un sistema completo. Las tareas para el hogar me ahorran horas de planificación.'
}];
type OfferKey = 'basic' | 'bump';
const offers: Record<OfferKey, {
  price: string;
  label: string;
  sublabel: string;
  href: string;
  includes: string[];
  bump?: {
    title: string;
    intro: string;
    items: {
      label: string;
      desc: string;
    }[];
    footer: string;
  };
}> = {
  basic: {
    price: '$19.990',
    label: 'Biblioteca Completa',
    sublabel: '12 tomos + 8 bonos',
    href: 'https://psicocheck.librosdigitales.pro/cart/45987854057551:1',
    includes: ['240 intervenciones clínicas completas (12 tomos)', '1.200 frases terapéuticas + 1.200 preguntas poderosas', '240 tareas para el hogar + 36 casos clínicos', 'Los 8 bonos imprimibles incluidos GRATIS', 'Acceso digital inmediato y de por vida']
  },
  bump: {
    price: '$29.990',
    label: 'Biblioteca + Acelerador Clínico',
    sublabel: 'Todo lo anterior + Order Bump',
    href: 'https://psicocheck.librosdigitales.pro/cart/45987908255823:1',
    includes: ['Todo lo de la Biblioteca Completa (12 tomos + 8 bonos)', '+ Acelerador Clínico de Tercera Generación (ACT + DBT)', '≈ 49 hojas A4 adicionales · Todo imprimible', 'Acceso digital inmediato y de por vida'],
    bump: {
      title: '⚡ Acelerador Clínico de Tercera Generación',
      intro: 'Para las pacientes que entienden todo pero no cambian nada. Basado en ACT y DBT, listo para usar en sesión sin formación previa.',
      items: [{
        label: 'Metáforas Disruptivas (ACT)',
        desc: 'Cinco intervenciones con hoja de trabajo para romper defensas cognitivas en sesión.'
      }, {
        label: 'Análisis de Cadena (DBT)',
        desc: 'El protocolo paso a paso para desarmar conductas impulsivas y diseñar el plan de cambio.'
      }, {
        label: 'Inventario de Valores (ACT)',
        desc: 'Rueda de la Vida, 60 Valores y Planificador de Acciones para trabajar desde el propósito.'
      }, {
        label: 'Kit TIPP (DBT)',
        desc: 'Regulación biológica de crisis más Aceptación Radical, Mente Sabia y Protocolo de Emergencia personal.'
      }],
      footer: '≈ 49 hojas A4 · Todo imprimible · Listo para usar mañana. Precio regular del Acelerador: $25.000 — solo $10.000 sumándolo en esta oferta.'
    }
  }
};
export function SalesPage() {
  const [popupVisible, setPopupVisible] = useState(false);
  const [buyer, setBuyer] = useState(buyers[0]);
  const [selectedOffer, setSelectedOffer] = useState<OfferKey>('basic');
  useEffect(() => {
    const showPopup = () => {
      setBuyer(buyers[Math.floor(Math.random() * buyers.length)]);
      setPopupVisible(true);
      setTimeout(() => setPopupVisible(false), 5000);
    };
    // Initial delay before first popup
    const initialTimeout = setTimeout(showPopup, 3000);
    // Then every 15 seconds
    const interval = setInterval(showPopup, 15000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);
  const scrollToCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('checkout-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="relative min-h-screen bg-pink-50/40 font-sans text-slate-800 selection:bg-pink-200 selection:text-pink-900" data-id="element-2">
      {/* Pink side glow */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-y-0 left-0 w-[24%] z-30 bg-[radial-gradient(ellipse_at_left,rgba(236,72,153,0.22),transparent_72%)]" data-id="element-3" />
      <div aria-hidden="true" className="pointer-events-none fixed inset-y-0 right-0 w-[24%] z-30 bg-[radial-gradient(ellipse_at_right,rgba(236,72,153,0.22),transparent_72%)]" data-id="element-4" />

      {/* Social Proof Popup */}
      <AnimatePresence data-id="element-5">
        {popupVisible && <motion.div initial={{
        opacity: 0,
        y: 50,
        x: -20
      }} animate={{
        opacity: 1,
        y: 0,
        x: 0
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className="fixed bottom-6 left-6 z-50 bg-white rounded-lg shadow-xl border border-pink-100 p-4 flex items-center gap-4 max-w-sm" data-id="element-6">
            <div className="bg-pink-100 p-2 rounded-full text-pink-600" data-id="element-7">
              <Users size={20} data-id="element-8" />
            </div>
            <div data-id="element-9">
              <p className="text-sm font-medium text-slate-800" data-id="element-10">
                <span className="font-bold" data-id="element-11">{buyer.name}</span> de {buyer.city}
              </p>
              <p className="text-xs text-slate-500" data-id="element-12">
                Acaba de comprar la Biblioteca
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Hero Section */}
      <header className="relative z-10 bg-white border-b border-slate-200 pt-16 pb-20 px-4" data-id="element-13">
        <div className="max-w-4xl mx-auto text-center" data-id="element-14">
          <div className="inline-block bg-pink-100 text-pink-700 px-4 py-1.5 rounded-full text-sm font-bold tracking-wide mb-6" data-id="element-15">
            PARA PSICÓLOGAS CLÍNICAS
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight mb-8 leading-tight" data-id="element-16">
            BIBLIOTECA DE INTERVENCIONES TERAPÉUTICAS
          </h1>

          <div className="max-w-2xl mx-auto text-lg md:text-xl text-slate-600 space-y-4 mb-10 text-left md:text-center" data-id="element-17">
            <p className="font-medium text-pink-600" data-id="element-18">
              ¿Y si el problema no fuera tu paciente, sino las herramientas que
              estás usando?
            </p>
            <p data-id="element-19">
              Accedé a intervenciones terapéuticas listas para aplicar,
              desarrolladas a partir de los casos clínicos que más se repiten
              hoy en consulta. Un sistema actualizado con recursos basados en
              situaciones reales para acompañar tu práctica con mayor seguridad,
              claridad y confianza.{' '}
              <strong className="font-semibold text-pink-600" data-id="element-20">
                No reemplaza tu criterio profesional: lo potencia.
              </strong>
            </p>
          </div>

          <a href="#checkout-section" onClick={scrollToCheckout} className="block cursor-pointer group" data-id="element-21">
            <div className="relative w-full max-w-2xl mx-auto rounded-2xl overflow-hidden mb-10 transition-transform duration-300 group-hover:scale-[1.02]" data-id="element-22">
              <img src="https://cdn.magicpatterns.com/uploads/1P993YPHqaCdigjo1BMXw8/WhatsApp_Image_2026-06-30_at_3.39.30_PM-removebg-preview.png" alt="Biblioteca de Intervenciones Terapéuticas — 12 tomos con material clínico imprimible y bonos" className="w-full h-auto object-contain" data-id="element-23" />
            </div>
          </a>

          <div className="max-w-2xl mx-auto mb-10" data-id="element-24">
            <p className="text-lg md:text-xl text-slate-700 font-medium" data-id="element-25">
              <span className="font-bold text-pink-600" data-id="element-26">
                12 tomos. 240 intervenciones clínicas listas para aplicar hoy
                mismo.
              </span>{' '}
              Acceso digital inmediato, de por vida, e imprimible para tu
              consultorio. Sin esperas, sin envíos: lo tenés en tus manos en
              menos de 2 minutos.
            </p>
          </div>

          <button onClick={scrollToCheckout} className="bg-pink-600 hover:bg-pink-700 text-white text-lg md:text-xl font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-3 mx-auto" data-id="element-27">
            QUIERO MIS INTERVENCIONES YA <ArrowRight size={24} data-id="element-28" />
          </button>
        </div>
      </header>

      {/* Problem Agitation */}
      <section className="relative z-10 py-20 px-4 bg-pink-50/40" data-id="element-29">
        <div className="max-w-3xl mx-auto" data-id="element-30">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center" data-id="element-31">
            HABLEMOS DE ELLAS.
          </h2>
          <p className="text-xl text-center text-slate-600 mb-12" data-id="element-32">
            De las mujeres que llegan a tu consultorio.
          </p>

          <div className="space-y-6" data-id="element-33">
            {[{
            title: 'La que lleva años eligiendo vínculos que la lastiman.',
            desc: 'Sabe que le hacen mal. Te lo dice ella misma. Y la semana siguiente vuelve con lo mismo.'
          }, {
            title: 'La que no puede decir que no.',
            desc: 'En el trabajo. Con su familia. En su pareja. Lleva tanto tiempo diciendo que sí que ya ni sabe cuándo quiere decir que no.'
          }, {
            title: 'La que se exige tanto que nunca alcanza.',
            desc: 'Logra todo lo que se propone. Y se destruye igual. Porque el estándar siempre sube justo cuando está a punto de llegar.'
          }, {
            title: 'La que siente mucho y no puede regularlo.',
            desc: 'Se desborda. Dice cosas que no quería decir. Después se odia por eso. Y el ciclo vuelve a empezar.'
          }, {
            title: 'La que lleva años cargando una culpa que no es suya.',
            desc: 'O que sí es suya, pero que ya pagó mil veces. Y no puede soltarla igual.'
          }, {
            title: 'La que logró todo lo que se propuso',
            desc: 'y sigue convencida de que en algún momento la van a descubrir como la fraude que cree que es.'
          }].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex gap-4" data-id="element-34">
                <div className="text-pink-600 shrink-0 mt-1" data-id="element-35">
                  <CheckCircle2 size={24} data-id="element-36" />
                </div>
                <div data-id="element-37">
                  <h3 className="font-bold text-lg text-slate-900 mb-1" data-id="element-38">
                    {item.title}
                  </h3>
                  <p className="text-slate-600" data-id="element-39">{item.desc}</p>
                </div>
              </div>)}
          </div>

          <div className="mt-16 text-center space-y-6" data-id="element-40">
            <p className="text-xl font-medium" data-id="element-41">
              Esas mujeres llegan a tu consultorio.
            </p>
            <p className="text-xl font-medium" data-id="element-42">
              Y el trabajo con ellas requiere intervenciones específicas.
            </p>

            <div className="bg-pink-900 text-white p-8 rounded-2xl mt-12 shadow-xl" data-id="element-43">
              <h3 className="text-2xl font-bold mb-6" data-id="element-44">
                EL PROBLEMA NO ES QUE NO SEPAS SOBRE ESTOS TEMAS.
              </h3>
              <p className="text-lg text-pink-200 mb-8" data-id="element-45">
                El problema es que saber sobre un tema y tener las
                intervenciones precisas para trabajarlo sesión a sesión son dos
                cosas completamente distintas.
              </p>

              <ul className="space-y-4 text-left" data-id="element-46">
                <li className="flex gap-3 items-start" data-id="element-47">
                  <span className="text-pink-300 mt-1" data-id="element-48">→</span>
                  <span data-id="element-49">
                    Podés saber todo sobre dependencia emocional y quedarte sin
                    saber qué hacer en el minuto 20 cuando tu paciente repite
                    por quinta vez "sé que me hace mal pero no puedo irme".
                  </span>
                </li>
                <li className="flex gap-3 items-start" data-id="element-50">
                  <span className="text-pink-300 mt-1" data-id="element-51">→</span>
                  <span data-id="element-52">
                    Podés conocer la teoría del apego y no tener la pregunta
                    exacta para la sesión donde tu paciente se queda en silencio
                    y vos tampoco sabés por dónde seguir.
                  </span>
                </li>
                <li className="flex gap-3 items-start" data-id="element-53">
                  <span className="text-pink-300 mt-1" data-id="element-54">→</span>
                  <span data-id="element-55">
                    Podés entender perfectamente el perfeccionismo y no tener la
                    intervención concreta para la mujer que lleva diez años
                    destruyéndose porque nunca alcanza su propio estándar.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Presentation */}
      <section className="relative z-10 py-20 px-4 bg-white border-y border-slate-200" data-id="element-56">
        <div className="max-w-4xl mx-auto text-center" data-id="element-57">
          <p className="text-pink-600 font-bold tracking-widest text-sm mb-4" data-id="element-58">
            EL TRABAJO CON MUJERES EN ESTOS TEMAS
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-59">
            REQUIERE HERRAMIENTAS ESPECÍFICAS PARA ESTOS TEMAS.
          </h2>

          <div className="mt-16 mb-12" data-id="element-60">
            <p className="text-xl text-slate-500 mb-4" data-id="element-61">PRESENTAMOS:</p>
            <h3 className="text-4xl md:text-5xl font-extrabold text-pink-700 mb-6" data-id="element-62">
              📚 BIBLIOTECA DE INTERVENCIONES TERAPÉUTICAS
            </h3>
            <p className="text-2xl text-slate-700 font-medium max-w-2xl mx-auto" data-id="element-63">
              La colección completa de intervención clínica aplicada para
              psicólogas que trabajan con mujeres.
            </p>
          </div>

          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-12" data-id="element-64">
            No es teoría sobre estos temas.{' '}
            <strong className="text-pink-600" data-id="element-65">
              Son las intervenciones concretas para trabajarlos.
            </strong>{' '}
            Sesión a sesión. Con cada una de esas mujeres.
          </p>

          <div className="inline-block bg-slate-900 text-white px-8 py-4 rounded-xl text-2xl font-bold mb-16 shadow-lg" data-id="element-66">
            12 TOMOS. CADA UNO PARA UNA DE ELLAS.
          </div>

          {/* Tomes Grid */}
          <div className="grid md:grid-cols-2 gap-8 text-left" data-id="element-67">
            {[{
            id: '01',
            title: 'ANSIEDAD',
            subtitle: 'Para la paciente que vive en alerta y no logra parar la cabeza.',
            questions: '¿Cómo trabajás con alguien que anticipa siempre lo peor? ¿Qué hacés cuando la preocupación se vuelve su forma de estar en el mundo? ¿Cómo intervenís en la rumiación que no la deja dormir? ¿Qué le decís a la que confunde calma con descuido?',
            fichas: 'El Tomo 01 tiene 20 fichas de intervención para trabajar la ansiedad de forma concreta y progresiva.'
          }, {
            id: '02',
            title: 'DEPENDENCIA EMOCIONAL',
            subtitle: 'Para la paciente que sabe que el vínculo la daña y no puede salir.',
            questions: "¿Qué hacés cuando lleva meses diciéndote lo mismo? ¿Cómo trabajás el miedo al abandono que la paraliza? ¿Qué decís cuando te dice 'sé que me hace mal pero lo amo'? ¿Cómo trabajás la identidad que construyó alrededor de ese vínculo? ¿Qué intervención usás cuando vuelve después de haber cortado?",
            fichas: 'El Tomo 02 tiene 20 fichas de intervención con el paso a paso exacto para cada uno de esos momentos.'
          }, {
            id: '03',
            title: 'AUTOESTIMA',
            subtitle: 'Para la paciente que se trata peor a sí misma que a cualquier otra persona.',
            questions: "¿Cómo trabajás con alguien que genuinamente no puede verse bien? ¿Qué hacés con la voz interna que la destruye? ¿Cómo intervenís cuando minimiza todo logro propio? ¿Qué le decís cuando dice 'soy un desastre'? ¿Cómo construís autoestima real, no afirmaciones vacías?",
            fichas: 'El Tomo 03 tiene 20 fichas de intervención para trabajar la autoestima desde adentro.'
          }, {
            id: '04',
            title: 'CULPA CRONIFICADA',
            subtitle: 'Para la paciente que carga una culpa que ya pagó mil veces y no puede soltar.',
            questions: '¿Cómo trabajás con alguien que se castiga por algo que no puede cambiar? ¿Qué hacés con la culpa que no lleva a ningún cambio, solo a la destrucción? ¿Cómo intervenís cuando la culpa es la forma en que cree que demuestra que le importa? ¿Cómo diferenciás responsabilidad de autocastigo?',
            fichas: 'El Tomo 04 tiene 20 fichas de intervención para trabajar la culpa sin invalidar lo que la paciente siente.'
          }, {
            id: '05',
            title: 'DUELO Y PÉRDIDA',
            subtitle: 'Para la paciente que perdió algo o a alguien y no encuentra cómo seguir.',
            questions: '¿Cómo acompañás un duelo que no avanza? ¿Qué hacés cuando la pérdida no es una muerte sino un vínculo, una etapa o una versión de sí misma? ¿Cómo intervenís cuando se quedó atrapada en una fase del duelo? ¿Qué le decís a la que siente que seguir adelante es traicionar lo perdido?',
            fichas: 'El Tomo 05 tiene 20 fichas de intervención para acompañar el duelo respetando los tiempos de cada paciente.'
          }, {
            id: '06',
            title: 'APEGO Y VÍNCULOS',
            subtitle: 'Para la paciente que repite los mismos patrones en cada relación.',
            questions: '¿Cómo trabajás con alguien que elige siempre el mismo tipo de vínculo? ¿Qué hacés con el patrón de apego que se activa una y otra vez? ¿Cómo intervenís cuando el miedo a la cercanía y el miedo al abandono conviven? ¿Cómo trabajás la historia temprana sin quedarte solo en la interpretación?',
            fichas: 'El Tomo 06 tiene 20 fichas de intervención para trabajar los patrones de apego en el vínculo presente.'
          }, {
            id: '07',
            title: 'LÍMITES SALUDABLES',
            subtitle: 'Para la paciente que dice que sí cuando quiere decir que no desde hace años.',
            questions: '¿Cómo trabajás con alguien que ni siquiera sabe que tiene derecho a decir que no? ¿Qué hacés con la culpa que siente cada vez que intenta poner un límite? ¿Cómo trabajás el miedo a perder el vínculo si se planta? ¿Qué intervención usás cuando el límite lo necesita con su propia familia? ¿Cómo trabajás los límites en una mujer que fue criada para complacer?',
            fichas: 'El Tomo 07 tiene 20 fichas de intervención para trabajar los límites sin que la paciente sienta que está siendo mala persona.'
          }, {
            id: '08',
            title: 'TRAUMA',
            subtitle: 'Para la paciente que carga algo que todavía la sigue habitando hoy.',
            questions: '¿Cómo trabajás con alguien que revive lo que pasó como si fuera ahora? ¿Qué hacés cuando el cuerpo recuerda lo que la mente quiere olvidar? ¿Cómo intervenís sin retraumatizar? ¿Cómo construís seguridad antes de tocar el contenido más doloroso?',
            fichas: 'El Tomo 08 tiene 20 fichas de intervención para trabajar el trauma de forma cuidada y por etapas.'
          }, {
            id: '09',
            title: 'IDENTIDAD Y AUTOCONOCIMIENTO',
            subtitle: 'Para la paciente que vivió tanto para los demás que ya no sabe quién es.',
            questions: '¿Cómo trabajás con alguien que no sabe qué quiere para sí misma? ¿Qué hacés cuando construyó su identidad alrededor de los otros? ¿Cómo intervenís cuando teme que conocerse implique perder lo que tiene? ¿Cómo acompañás la pregunta "¿quién soy yo cuando no estoy cuidando a nadie?"',
            fichas: 'El Tomo 09 tiene 20 fichas de intervención para trabajar la identidad y el reencuentro con el propio deseo.'
          }, {
            id: '10',
            title: 'AUTOEXIGENCIA Y PERFECCIONISMO',
            subtitle: 'Para la paciente que nunca alcanza el estándar que ella misma se impone.',
            questions: '¿Cómo trabajás con alguien que logra todo y se destruye igual? ¿Qué hacés con el miedo al fracaso que la paraliza antes de intentar? ¿Cómo intervenís en el ciclo esfuerzo → logro → no internalización → más exigencia? ¿Qué le decís a la que nunca puede descansar porque siempre hay más por hacer? ¿Cómo trabajás la diferencia entre excelencia y autoexigencia destructiva?',
            fichas: 'El Tomo 10 tiene 20 fichas de intervención para trabajar la autoexigencia sin que la paciente sienta que le estás pidiendo que baje el estándar.'
          }, {
            id: '11',
            title: 'REGULACIÓN EMOCIONAL',
            subtitle: 'Para la paciente que siente mucho, se desborda, y después se destruye por eso.',
            questions: "¿Cómo trabajás con alguien que no puede nombrar lo que siente? ¿Qué hacés cuando se desregula en sesión? ¿Cómo enseñás regulación sin que suene a 'respirá y ya'? ¿Qué intervención usás cuando el cuerpo carga lo que la mente no puede procesar? ¿Cómo trabajás la ventana de tolerancia con alguien que la tiene muy estrecha?",
            fichas: 'El Tomo 11 tiene 20 fichas de intervención para trabajar la regulación emocional de forma concreta y progresiva.'
          }, {
            id: '12',
            title: 'SÍNDROME DE LA IMPOSTORA',
            subtitle: 'Para la paciente que logró todo lo que se propuso y sigue convencida de que la van a descubrir.',
            questions: '¿Cómo trabajás con alguien que tiene evidencia objetiva de competencia y no puede internalizarla? ¿Qué hacés cuando todo logro lo atribuye a la suerte? ¿Cómo intervenís en el miedo a ser descubierta como fraude? ¿Qué le decís a la que lleva 15 años de carrera y sigue sintiéndose impostora en cada reunión? ¿Cómo trabajás el ciclo sobre-esfuerzo → logro → no internalización?',
            fichas: 'El Tomo 12 tiene 20 fichas de intervención para trabajar el síndrome de la impostora con la profundidad que merece.'
          }].map(tomo => <div key={tomo.id} className="bg-pink-50/40 rounded-2xl p-8 border border-slate-200 hover:border-pink-300 transition-colors" data-id="element-68">
                <div className="text-pink-600 font-bold mb-2" data-id="element-69">
                  TOMO {tomo.id}
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-70">
                  {tomo.title}
                </h4>
                <p className="text-slate-700 font-medium mb-6" data-id="element-71">
                  {tomo.subtitle}
                </p>
                <div className="bg-white p-4 rounded-lg text-sm text-slate-600 italic mb-6 border border-slate-100" data-id="element-72">
                  {tomo.questions}
                </div>
                <p className="text-pink-700 font-medium bg-pink-100 p-4 rounded-lg" data-id="element-73">
                  {tomo.fichas}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Inside Each Tome */}
      <section className="relative z-10 py-20 px-4 bg-slate-900 text-white" data-id="element-74">
        <div className="max-w-4xl mx-auto" data-id="element-75">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-76">
            DENTRO DE CADA TOMO:
          </h2>

          <div className="space-y-12" data-id="element-77">
            {/* Fichas */}
            <div data-id="element-78">
              <h3 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-3" data-id="element-79">
                <span className="text-3xl" data-id="element-80">📋</span> 20 FICHAS DE INTERVENCIÓN
              </h3>
              <div className="grid md:grid-cols-2 gap-6" data-id="element-81">
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700" data-id="element-82">
                  <h4 className="font-bold text-lg mb-4 text-slate-300" data-id="element-83">
                    La hoja para vos:
                  </h4>
                  <ul className="space-y-3 text-slate-400" data-id="element-84">
                    <li className="flex gap-2" data-id="element-85">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-86" />{' '}
                      El objetivo clínico de la intervención
                    </li>
                    <li className="flex gap-2" data-id="element-87">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-88" />{' '}
                      Cuándo usarla — el momento exacto del proceso
                    </li>
                    <li className="flex gap-2" data-id="element-89">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-90" />{' '}
                      El paso a paso de la intervención
                    </li>
                    <li className="flex gap-2" data-id="element-91">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-92" />{' '}
                      Qué decir — frases textuales para ese momento
                    </li>
                    <li className="flex gap-2" data-id="element-93">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-94" />{' '}
                      Qué preguntar — para ir más profundo
                    </li>
                    <li className="flex gap-2" data-id="element-95">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-96" />{' '}
                      Qué observar clínicamente
                    </li>
                    <li className="flex gap-2" data-id="element-97">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-98" />{' '}
                      Qué evitar
                    </li>
                    <li className="flex gap-2" data-id="element-99">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-100" />{' '}
                      Cómo cerrar la sesión
                    </li>
                  </ul>
                </div>
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700" data-id="element-101">
                  <h4 className="font-bold text-lg mb-4 text-slate-300" data-id="element-102">
                    La hoja para tu paciente:
                  </h4>
                  <ul className="space-y-3 text-slate-400" data-id="element-103">
                    <li className="flex gap-2" data-id="element-104">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-105" />{' '}
                      Actividad guiada lista para imprimir y entregar
                    </li>
                    <li className="flex gap-2" data-id="element-106">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-107" />{' '}
                      Diseño A4 imprimible y fotocopiable
                    </li>
                    <li className="flex gap-2" data-id="element-108">
                      <CheckCircle2 size={20} className="text-pink-500 shrink-0" data-id="element-109" />{' '}
                      Frase terapéutica del cierre
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Frases */}
            <div data-id="element-110">
              <h3 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-3" data-id="element-111">
                <span className="text-3xl" data-id="element-112">🗣️</span> 100 FRASES TERAPÉUTICAS
              </h3>
              <p className="text-slate-300 mb-6 text-lg" data-id="element-113">
                Frases reales para los momentos reales.
              </p>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-114">
                <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-pink-500" data-id="element-115">
                  <p className="text-sm text-slate-400 mb-2" data-id="element-116">
                    Para la paciente con dependencia emocional:
                  </p>
                  <p className="font-medium italic" data-id="element-117">
                    "No te quedaste porque sos débil. Te quedaste porque
                    aprendiste a sobrevivir ahí dentro."
                  </p>
                </div>
                <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-pink-500" data-id="element-118">
                  <p className="text-sm text-slate-400 mb-2" data-id="element-119">
                    Para la paciente con autoestima dañada:
                  </p>
                  <p className="font-medium italic" data-id="element-120">
                    "Lo que hiciste estuvo mal. Eso no significa que seas un
                    desastre. Son cosas muy distintas."
                  </p>
                </div>
                <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-pink-500" data-id="element-121">
                  <p className="text-sm text-slate-400 mb-2" data-id="element-122">
                    Para la paciente con culpa cronificada:
                  </p>
                  <p className="font-medium italic" data-id="element-123">
                    "La culpa que seguís sintiendo no dice que lo que pasó fue
                    grave. Dice que sos alguien a quien le importa."
                  </p>
                </div>
                <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-pink-500" data-id="element-124">
                  <p className="text-sm text-slate-400 mb-2" data-id="element-125">
                    Para la paciente con síndrome de la impostora:
                  </p>
                  <p className="font-medium italic" data-id="element-126">
                    "Si fueras una fraude, no te preocuparía tanto ser
                    descubierta."
                  </p>
                </div>
              </div>
            </div>

            {/* Preguntas */}
            <div data-id="element-127">
              <h3 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-3" data-id="element-128">
                <span className="text-3xl" data-id="element-129">🔍</span> 100 PREGUNTAS PODEROSAS
              </h3>
              <p className="text-slate-300 mb-6 text-lg" data-id="element-130">
                Cada pregunta con su para qué.
              </p>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-131">
                <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-rose-400" data-id="element-132">
                  <p className="text-sm text-slate-400 mb-2" data-id="element-133">
                    Para la paciente que no puede salir del vínculo:
                  </p>
                  <p className="font-medium italic" data-id="element-134">
                    "¿Qué te da ese vínculo que creés que no podés conseguir de
                    otra manera?"
                  </p>
                </div>
                <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-rose-400" data-id="element-135">
                  <p className="text-sm text-slate-400 mb-2" data-id="element-136">
                    Para la paciente con autoexigencia:
                  </p>
                  <p className="font-medium italic" data-id="element-137">
                    "¿Cuándo fue la última vez que te permitiste hacer algo bien
                    sin que fuera perfecto?"
                  </p>
                </div>
                <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-rose-400" data-id="element-138">
                  <p className="text-sm text-slate-400 mb-2" data-id="element-139">
                    Para la paciente que no puede poner límites:
                  </p>
                  <p className="font-medium italic" data-id="element-140">
                    "¿Qué creés que va a pasar si decís que no? ¿Y cuántas veces
                    pasó realmente eso?"
                  </p>
                </div>
                <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-rose-400" data-id="element-141">
                  <p className="text-sm text-slate-400 mb-2" data-id="element-142">
                    Para la paciente con culpa:
                  </p>
                  <p className="font-medium italic" data-id="element-143">
                    "¿La culpa que sentís te llevó a cambiar algo o solo te
                    llevó a destruirte?"
                  </p>
                </div>
              </div>
            </div>

            {/* Other features */}
            <div className="grid md:grid-cols-3 gap-6" data-id="element-144">
              <div className="bg-slate-800 p-6 rounded-xl border border-slate-700" data-id="element-145">
                <h3 className="text-xl font-bold text-pink-400 mb-3 flex items-center gap-2" data-id="element-146">
                  📋 20 TAREAS
                </h3>
                <p className="text-slate-400 text-sm" data-id="element-147">
                  Listas para entregar al final de la sesión. Diseñadas para que
                  el trabajo continúe en la semana. Con consigna clara, registro
                  y seguimiento.
                </p>
              </div>
              <div className="bg-slate-800 p-6 rounded-xl border border-slate-700" data-id="element-148">
                <h3 className="text-xl font-bold text-pink-400 mb-3 flex items-center gap-2" data-id="element-149">
                  📖 3 CASOS CLÍNICOS
                </h3>
                <p className="text-slate-400 text-sm" data-id="element-150">
                  Mujeres reales con sus complejidades. Con hipótesis clínica,
                  ruta de intervención y frases clave para cada perfil.
                </p>
              </div>
              <div className="bg-slate-800 p-6 rounded-xl border border-slate-700" data-id="element-151">
                <h3 className="text-xl font-bold text-pink-400 mb-3 flex items-center gap-2" data-id="element-152">
                  📄 7 IMPRIMIBLES
                </h3>
                <ul className="text-slate-400 text-sm space-y-1" data-id="element-153">
                  <li data-id="element-154">✅ Escala inicial</li>
                  <li data-id="element-155">✅ Diario semanal</li>
                  <li data-id="element-156">✅ Hoja de trabajo</li>
                  <li data-id="element-157">✅ Tarjetas para llevar</li>
                  <li data-id="element-158">✅ Plantilla de sesión</li>
                  <li data-id="element-159">✅ Recurso central</li>
                  <li data-id="element-160">✅ Hoja de objetivos</li>
                </ul>
              </div>
            </div>

            {/* Total Box */}
            <div className="mt-12 bg-gradient-to-r from-pink-900 to-rose-800 p-8 rounded-2xl border border-pink-500 shadow-2xl text-center" data-id="element-161">
              <h3 className="text-2xl font-bold mb-6 text-white" data-id="element-162">EN TOTAL:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-lg font-medium text-pink-100 max-w-2xl mx-auto text-left" data-id="element-163">
                <div className="flex items-center gap-3" data-id="element-164">
                  <CheckCircle2 className="text-pink-300" data-id="element-165" /> 240 intervenciones
                  clínicas completas
                </div>
                <div className="flex items-center gap-3" data-id="element-166">
                  <CheckCircle2 className="text-pink-300" data-id="element-167" /> 1.200 frases
                  terapéuticas
                </div>
                <div className="flex items-center gap-3" data-id="element-168">
                  <CheckCircle2 className="text-pink-300" data-id="element-169" /> 1.200 preguntas
                  poderosas
                </div>
                <div className="flex items-center gap-3" data-id="element-170">
                  <CheckCircle2 className="text-pink-300" data-id="element-171" /> 240 tareas para el
                  hogar
                </div>
                <div className="flex items-center gap-3" data-id="element-172">
                  <CheckCircle2 className="text-pink-300" data-id="element-173" /> 36 casos clínicos
                </div>
                <div className="flex items-center gap-3" data-id="element-174">
                  <CheckCircle2 className="text-pink-300" data-id="element-175" /> ≈ 757 hojas A4 de
                  material clínico
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 py-20 px-4 bg-pink-50/40" data-id="element-176">
        <div className="max-w-6xl mx-auto" data-id="element-177">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12" data-id="element-178">
            Lo que dicen las colegas que ya la usan
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-179">
            {testimonials.map((t, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200" data-id="element-180">
                <div className="flex text-pink-500 mb-4" data-id="element-181">
                  {[...Array(5)].map((_, idx) => <Star key={idx} size={16} fill="currentColor" data-id="element-182" />)}
                </div>
                <p className="text-slate-700 italic mb-6" data-id="element-183">"{t.text}"</p>
                <div data-id="element-184">
                  <p className="font-bold text-slate-900" data-id="element-185">{t.name}</p>
                  <p className="text-sm text-slate-500" data-id="element-186">{t.role}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="relative z-10 py-20 px-4 bg-white" data-id="element-187">
        <div className="max-w-5xl mx-auto text-center" data-id="element-188">
          <h2 className="text-4xl font-extrabold text-slate-900 mb-4" data-id="element-189">
            🎁 MÁS LOS 8 BONOS
          </h2>
          <p className="text-xl text-pink-600 font-bold mb-12" data-id="element-190">
            Estos bonos son GRATIS por el día de hoy.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-191">
            {[{
            id: 'B01',
            title: 'Registro de Avances',
            desc: 'Cuando tu paciente siente que no avanza',
            image: 'https://cdn.magicpatterns.com/uploads/guNeqPNpbmMy3zhgXKyEJ2/253cbe0a-49ce-4a9f-86b5-2c39ce85ee58.png'
          }, {
            id: 'B02',
            title: 'Cuaderno Emocional',
            desc: 'Para que el trabajo continúe entre sesiones',
            image: 'https://cdn.magicpatterns.com/uploads/tQ18NK85h1iMeb1sheV7Ps/0c66350d-556d-4ee1-b046-627ee4d84941-removebg-preview.png'
          }, {
            id: 'B03',
            title: 'Banco de Preguntas de Exploración Clínica',
            desc: 'Para obtener información clave y comprender a tu paciente',
            image: 'https://cdn.magicpatterns.com/uploads/m3N6ZNTuLHWURJTdMhcgQ6/aff3e600-ec61-4fad-9986-91f4ce8f15e8-removebg-preview.png'
          }, {
            id: 'B04',
            title: 'Mapa de Fortalezas Terapéuticas',
            desc: 'Mapa de fortalezas terapéuticas',
            image: 'https://cdn.magicpatterns.com/uploads/wwhVkzvkyq9pZWvHjpY2XT/187fc7fe-9d77-472f-a860-e60d17998e04-removebg-preview.png'
          }, {
            id: 'B05',
            title: 'Registro de Seguimiento Terapéutico',
            desc: 'Para hacer seguimiento del proceso sesión a sesión',
            image: 'https://cdn.magicpatterns.com/uploads/jbtcC5d4vvkoiZjzdiqE5y/d0e692ff-ce66-41fa-b391-ff8d9e7d1702-removebg-preview.png'
          }, {
            id: 'B06',
            title: 'Planificador de Sesiones',
            desc: 'Para organizar y estructurar cada encuentro',
            image: 'https://cdn.magicpatterns.com/uploads/8TiZewDgu1JXufe6EiUUqo/aa889526-7654-4d2d-8994-615a524df5d4-removebg-preview.png'
          }, {
            id: 'B07',
            title: 'Biblioteca de Recursos Imprimibles',
            desc: 'Para tener siempre a mano materiales listos para usar',
            image: 'https://cdn.magicpatterns.com/uploads/x5fdHV1qG8Xccay2ssgZ1o/b0a9b9a1-f65e-4c72-9000-19eca0ae82e7-removebg-preview.png'
          }, {
            id: 'B08',
            title: 'Guía de Intervenciones Breves',
            desc: 'Para aplicar estrategias efectivas en menos tiempo',
            image: 'https://cdn.magicpatterns.com/uploads/nXh4zPDxGRhALRuJ7NtQFX/87e963c7-b7a0-4694-a886-63d67f36effa-removebg-preview.png'
          }].map(bono => <a href="#checkout-section" onClick={scrollToCheckout} key={bono.id} className="block group cursor-pointer" data-id="element-192">
                <div className="bg-pink-50/40 rounded-xl overflow-hidden border border-slate-200 hover:border-pink-400 transition-colors h-full flex flex-col" data-id="element-193">
                  <div className="h-56 bg-white flex items-center justify-center py-4 text-center text-pink-400 relative overflow-hidden" data-id="element-194">
                    {bono.image ? <img src={bono.image} alt={`Bono ${bono.id.replace('B', '')} — ${bono.title}`} className="h-full w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]" data-id="element-195" /> : <>
                        <div className="absolute inset-0 bg-pink-100/60" data-id="element-196"></div>
                        <div className="absolute inset-0 bg-pink-900/5 group-hover:bg-pink-900/10 transition-colors z-10" data-id="element-197"></div>
                        <div className="flex flex-col items-center gap-2 relative z-20" data-id="element-198">
                          <BookOpen size={32} className="opacity-50" data-id="element-199" />
                          <span className="text-sm font-medium" data-id="element-200">
                            Imagen Bono {bono.id}
                            <br data-id="element-201" />
                            (Placeholder)
                          </span>
                        </div>
                      </>}
                  </div>
                  <div className="p-5 flex-1 flex flex-col justify-center" data-id="element-202">
                    <div className="text-xs font-bold text-pink-600 mb-1" data-id="element-203">
                      BONO {bono.id.replace('B', '')}
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2" data-id="element-204">
                      {bono.title}
                    </h4>
                    <p className="text-sm text-slate-600" data-id="element-205">{bono.desc}</p>
                  </div>
                </div>
              </a>)}
          </div>
        </div>
      </section>

      {/* Closing & Checkout */}
      <section id="checkout-section" className="relative z-10 py-24 px-4 bg-white" data-id="element-206">
        <div className="max-w-3xl mx-auto text-center" data-id="element-207">
          <h2 className="text-3xl font-bold text-slate-900 mb-10" data-id="element-208">
            UNA ÚLTIMA COSA:
          </h2>

          <div className="space-y-4 text-lg text-slate-700 mb-12 text-left md:text-center max-w-2xl mx-auto" data-id="element-209">
            <p data-id="element-210">
              La próxima vez que tu paciente llegue y diga por quinta vez lo
              mismo.
            </p>
            <p data-id="element-211">
              La próxima vez que quieras moverla del lugar donde lleva meses
              estancada.
            </p>
            <p data-id="element-212">
              La próxima vez que necesites la pregunta exacta, la frase exacta,
              la intervención exacta para ese momento específico con esa mujer
              específica.
            </p>
            <p className="font-bold text-pink-600 text-xl mt-8" data-id="element-213">
              Lo vas a tener.
            </p>
          </div>

          <div className="bg-pink-50/40 p-6 md:p-10 rounded-3xl border-2 border-pink-100 shadow-xl relative overflow-hidden text-left" data-id="element-214">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-500 to-rose-400" data-id="element-215"></div>

            <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center" data-id="element-216">
              Biblioteca de Intervenciones Terapéuticas.
            </h3>
            <p className="text-slate-600 mb-8 text-center" data-id="element-217">
              Elegí la opción que mejor se adapta a vos.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6" data-id="element-218">
              {(Object.keys(offers) as OfferKey[]).map(key => {
              const offer = offers[key];
              const active = selectedOffer === key;
              return <button key={key} type="button" onClick={() => setSelectedOffer(key)} aria-pressed={active} className={`relative text-left rounded-2xl border-2 p-5 transition-all ${active ? 'border-pink-500 bg-white shadow-md ring-2 ring-pink-200' : 'border-slate-200 bg-white/60 hover:border-pink-300'}`} data-id="element-219">
                    {key === 'bump' && <span className="absolute -top-3 right-4 bg-pink-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full" data-id="element-220">
                        MÁS ELEGIDA
                      </span>}
                    <div className="flex items-start gap-3" data-id="element-221">
                      <span className={`mt-1 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? 'border-pink-600 bg-pink-600' : 'border-slate-300'}`} data-id="element-222">
                        {active && <span className="w-2 h-2 rounded-full bg-white" data-id="element-223" />}
                      </span>
                      <div data-id="element-224">
                        <p className="font-bold text-slate-900" data-id="element-225">
                          {offer.label}
                        </p>
                        <p className="text-xs text-slate-500 mb-2" data-id="element-226">
                          {offer.sublabel}
                        </p>
                        <p className="text-2xl font-extrabold text-pink-600" data-id="element-227">
                          {offer.price}{' '}
                          <span className="text-sm text-slate-400 font-medium" data-id="element-228">
                            ARS
                          </span>
                        </p>
                      </div>
                    </div>
                  </button>;
            })}
            </div>

            <AnimatePresence mode="wait" data-id="element-229">
              <motion.div key={selectedOffer} initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.25
            }} className="overflow-hidden" data-id="element-230">
                <div className="bg-white rounded-2xl border border-pink-100 p-5 mb-6" data-id="element-231">
                  <p className="text-sm font-bold text-slate-900 mb-3" data-id="element-232">
                    Esto es lo que te llevás:
                  </p>
                  <ul className="space-y-2 mb-2" data-id="element-233">
                    {offers[selectedOffer].includes.map((item, i) => <li key={i} className="flex gap-2 text-sm text-slate-700" data-id="element-234">
                        <CheckCircle2 size={18} className="text-pink-500 shrink-0 mt-0.5" data-id="element-235" />
                        <span data-id="element-236">{item}</span>
                      </li>)}
                  </ul>

                  {offers[selectedOffer].bump ? <div className="mt-4 pt-4 border-t border-dashed border-pink-200" data-id="element-237">
                      <p className="font-bold text-pink-700 mb-1" data-id="element-238">
                        {offers[selectedOffer].bump?.title}
                      </p>
                      <p className="text-sm text-slate-600 mb-3" data-id="element-239">
                        {offers[selectedOffer].bump?.intro}
                      </p>
                      <ul className="space-y-2" data-id="element-240">
                        {offers[selectedOffer].bump?.items.map((it, i) => <li key={i} className="flex gap-2 text-sm text-slate-700" data-id="element-241">
                            <span className="text-pink-500 font-bold shrink-0" data-id="element-242">
                              ✅
                            </span>
                            <span data-id="element-243">
                              <strong className="text-slate-900" data-id="element-244">
                                {it.label}:
                              </strong>{' '}
                              {it.desc}
                            </span>
                          </li>)}
                      </ul>
                      <p className="text-xs text-slate-500 mt-3 italic" data-id="element-245">
                        {offers[selectedOffer].bump?.footer}
                      </p>
                    </div> : null}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="text-center mb-4" data-id="element-246">
              <p className="text-sm text-slate-500 uppercase tracking-wider font-bold mb-1" data-id="element-247">
                TOTAL
              </p>
              <div className="text-4xl font-extrabold text-slate-900 flex items-center justify-center gap-2" data-id="element-248">
                {offers[selectedOffer].price}{' '}
                <span className="text-lg text-slate-500 font-medium" data-id="element-249">ARS</span>
              </div>
            </div>

            <a href={offers[selectedOffer].href} className="block w-full bg-pink-600 hover:bg-pink-700 text-white text-xl font-bold py-5 px-8 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all text-center" data-id="element-250">
              QUIERO LA BIBLIOTECA AHORA
            </a>

            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500" data-id="element-251">
              <Clock size={16} data-id="element-252" />
              <span data-id="element-253">Acceso inmediato y de por vida</span>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="relative z-10 py-16 px-4 bg-pink-50/40 border-y border-slate-200" data-id="element-254">
        <div className="max-w-3xl mx-auto text-center flex flex-col items-center" data-id="element-255">
          <ShieldCheck size={64} className="text-pink-600 mb-6" data-id="element-256" />
          <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-257">GARANTÍA:</h2>
          <p className="text-lg text-slate-700" data-id="element-258">
            Si después de revisarlo sentís que no es lo que esperabas,{' '}
            <strong className="text-pink-600" data-id="element-259">te devolvemos el dinero.</strong>{' '}
            Sin preguntas. Sin trámites. Sin vueltas.
          </p>
        </div>
      </section>

      <footer className="relative z-10 bg-slate-900 text-slate-400 py-8 text-center text-sm" data-id="element-260">
        <p data-id="element-261">
          © {new Date().getFullYear()} Biblioteca de Intervenciones
          Terapéuticas. Todos los derechos reservados.
        </p>
      </footer>
    </div>;
}